import {
  type BigFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ONE,
  FRACTION_TWELVE,
} from "@aprao/common/src/math/fraction.js";
import { wrapFraction } from "@aprao/common/src/math/wrapFraction.js";
import type {
  CountableNumber,
  PeriodGranularity,
  WeekCount,
} from "@aprao/common/src/shapes/common.js";
import { combineNullables } from "@aprao/common/src/util/combineNullables.js";
import { makeAutoObservable } from "mobx";
import { mapOrForNullable } from "option-t/nullable";

import type { ShapeOperatedAssetItem } from "../../shapes/ShapeRevenueGroup.js";
import { relativesSetFacadeWithAbsolute } from "../RelativesSetFacade.js";
import { RevenueItemBaseFacade } from "./RevenueItemBaseFacade.js";

const WEEK_COUNT_IN_A_YEAR = bigFractionFromNumber(52);

export const PERIOD_COUNTS_IN_YEAR = {
  annual: FRACTION_ONE,
  monthly: FRACTION_TWELVE,
  weekly: WEEK_COUNT_IN_A_YEAR,
} as const satisfies Record<PeriodGranularity, BigFraction>;

export class OperatedAssetItemFacade {
  readonly $model;

  readonly _revenueItemBase;

  readonly _providers;

  constructor(
    model: ShapeOperatedAssetItem,
    providers: {
      periodGranularity: () => PeriodGranularity;
    },
  ) {
    makeAutoObservable(this);

    this.$model = model;

    this._providers = providers;
    this._revenueItemBase = new RevenueItemBaseFacade(this.$model);
  }

  get area() {
    return wrapFraction(this.$model.area);
  }

  set area(area: BigFraction | null) {
    this.$model.area = area?.toFraction() ?? null;
  }

  get periodRent() {
    return relativesSetFacadeWithAbsolute(this.$model.periodRent, {
      ratioArea: () => this.area,
    });
  }

  get weekLetCount() {
    return this.$model.weekLetCount;
  }

  set weekLetCount(count: WeekCount | null) {
    this.$model.weekLetCount = count;
  }

  get aggregateArea() {
    return this.area?.mul(this.quantity) ?? null;
  }

  get aggregateAnnualRentalIncome() {
    const occupancyRatio = mapOrForNullable(
      this.weekLetCount,
      FRACTION_ONE,
      (weekLetCount) =>
        bigFractionFromNumber(weekLetCount).div(WEEK_COUNT_IN_A_YEAR),
    );

    return combineNullables(
      (periodRent, quantity) =>
        periodRent
          .mul(quantity)
          .mul(PERIOD_COUNTS_IN_YEAR[this._providers.periodGranularity()])
          // the annual rent is potentially reduced depending on the week occupancy
          .mul(occupancyRatio),
      this.periodRent.relativesSet.getRelative("absolute"),
      this.quantity,
    );
  }

  // below are all the delegates
  get description() {
    return this._revenueItemBase.description;
  }

  set description(description: string) {
    this._revenueItemBase.description = description;
  }

  get quantity() {
    return this._revenueItemBase.quantity;
  }

  set quantity(unitCount: CountableNumber) {
    this._revenueItemBase.quantity = unitCount;
  }
}
